/**
 * Subscriptions Service
 * Handles Razorpay subscription and Nitya Seva subscription-related API calls
 */

import { ApiClient } from '../core/api-client';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export interface SubscriptionPlan {
  id: string;
  name: string;
  type: string;
  amount?: number;
  interval?: string;
  currency?: string;
  notes?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface Subscription {
  id: string;
  planId: string;
  customerId?: string;
  status: string;
  createdAt?: string;
  [key: string]: unknown;
}

export interface VerificationPayload {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface SubscriptionDonation {
  donation?: unknown;
  subscription?: unknown;
  [key: string]: unknown;
}

export interface NityaSevaHistoryDonation {
  id?: number;
  date?: string;
  amount?: number;
  paymentMethod?: string;
  status?: string;
  head?: string;
  purpose?: string;
  note?: string;
  createdAt?: string;
  txnRefId?: string;
  referenceId?: string;
}

export interface NityaSevaHistorySubscription {
  id?: number;
  razorpayId?: string;
  type?: string;
  head?: string;
  status?: string;
  amount?: number;
  chargeAt?: string;
}

export interface NityaSevaDonationHistoryItem {
  kind?: 'paid' | 'due';
  month?: string;
  status?: string;
  displayDate?: string;
  amount?: number;
  paymentMethod?: string;
  canPayOnline?: boolean;
  dueReason?: string;
  donation?: NityaSevaHistoryDonation;
  subscription?: NityaSevaHistorySubscription;
  meta?: Record<string, string>;
  [key: string]: unknown;
}

export interface RazorpayOrder {
  id: string;
  amount: number;
  currency: string;
  status?: string;
  notes?: Record<string, string>;
}

export interface PaymentVerificationPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface PaymentVerificationResponse {
  status: string;
  error?: string;
}

export interface NityaSevaEnrollmentResponse {
  enrollment?: NityaSevaEnrollment | null;
  donationHistory?: NityaSevaDonationHistoryItem[];
}

export type NityaSevaPaymentFrequency = 'monthly' | 'quarterly' | 'half_yearly' | 'annually';

export interface NityaSevaEnrollment {
  id: number;
  userId: number;
  fullName: string;
  whatsAppNumber: string;
  phone?: string;
  email: string;
  address: string;
  dateOfBirth: string;
  anniversaryDate?: string;
  pledgeTier: string;
  pledgeAmount: number;
  paymentFrequency: NityaSevaPaymentFrequency;
  submissionMode: 'ecs_nach' | 'auto_razorpay' | 'online_transfer' | 'cash_first_sunday';
  panNumber?: string;
  status: string;
  enrollmentSource?: string;
  adminNote?: string;
  lastManualDonationAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface NityaSevaEnrollmentPayload {
  anniversaryDate?: string;
  pledgeTier: string;
  pledgeAmount: number;
  paymentFrequency: NityaSevaPaymentFrequency;
  submissionMode: 'ecs_nach' | 'auto_razorpay' | 'online_transfer' | 'cash_first_sunday';
}

class SubscriptionsService {
  private readonly client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Get user subscriptions
   */
  async getUserSubscriptions(signal?: AbortSignal): Promise<Subscription[] | null> {
    try {
      const response = await this.client.get<unknown>(
        '/user/subscriptions',
        { signal },
      );
      return Array.isArray(response.data) ? (response.data as Subscription[]) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get subscription plans
   */
  async getSubscriptionPlans(
    type: string = 'Nitya Seva',
    signal?: AbortSignal,
  ): Promise<SubscriptionPlan[]> {
    try {
      const response = await this.client.get<unknown>(
        `/subscription/plans?type=${encodeURIComponent(type)}`,
        { signal },
      );

      if (Array.isArray(response.data)) {
        return response.data as SubscriptionPlan[];
      }

      if (response.data && typeof response.data === 'object' && 'items' in response.data) {
        const items = (response.data as { items?: unknown }).items;
        return Array.isArray(items) ? (items as SubscriptionPlan[]) : [];
      }

      return [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Create a Razorpay plan
   */
  async createRazorpayPlan(amount: number, signal?: AbortSignal): Promise<unknown> {
    const response = await this.client.post<unknown>(
      '/razorpay/plan',
      {
        amount,
        interval: 'monthly',
        currency: 'INR',
        type: 'Nitya Seva',
        head: 'Nitya Seva',
      },
      { signal },
    );
    return response.data;
  }

  /**
   * Create a Razorpay subscription
   */
  async createRazorpaySubscription(
    planId: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const response = await this.client.post<unknown>(
      '/razorpay/subscription',
      {
        planId,
        customerNotify: 1,
        type: 'Nitya Seva',
        head: 'Nitya Seva',
        notes: { userId, head: 'Nitya Seva', purpose: 'Nitya Seva' },
      },
      { signal },
    );
    return response.data;
  }

  /**
   * Verify Razorpay subscription
   */
  async verifyRazorpaySubscription(
    payload: VerificationPayload,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const response = await this.client.post<unknown>(
      '/razorpay/verify-subscription',
      payload,
      { signal },
    );
    return response.data;
  }

  /**
   * Cancel Razorpay subscription
   */
  async cancelRazorpaySubscription(
    id: string,
    cancelAtCycleEnd: boolean = false,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const response = await this.client.post<unknown>(
      `/razorpay/subscriptions/${id}/cancel`,
      { cancelAtCycleEnd },
      { signal },
    );
    return response.data;
  }

  async pauseRazorpaySubscription(
    id: string,
    signal?: AbortSignal,
  ): Promise<unknown> {
    const response = await this.client.post<unknown>(
      `/razorpay/subscriptions/${id}/pause`,
      {},
      { signal },
    );
    return response.data;
  }

  /**
   * Get subscription donations history
   */
  async getSubscriptionDonations(signal?: AbortSignal): Promise<SubscriptionDonation[] | null> {
    try {
      const response = await this.client.get<unknown>(
        '/user/subscription/donations',
        { signal },
      );
      return Array.isArray(response.data) ? (response.data as SubscriptionDonation[]) : null;
    } catch (error) {
      return null;
    }
  }

  async getNityaSevaDonationHistory(signal?: AbortSignal): Promise<NityaSevaDonationHistoryItem[] | null> {
    try {
      const response = await this.client.get<unknown>(
        '/user/nityaseva/donations',
        { signal },
      );
      return Array.isArray(response.data) ? (response.data as NityaSevaDonationHistoryItem[]) : null;
    } catch (error) {
      return null;
    }
  }

  async getNityaSevaEnrollment(signal?: AbortSignal): Promise<NityaSevaEnrollmentResponse> {
    try {
      const response = await this.client.get<NityaSevaEnrollmentResponse>(
        '/user/nityaseva/enrollment',
        { signal },
      );
      return {
        enrollment: response.data?.enrollment ?? null,
        donationHistory: Array.isArray(response.data?.donationHistory) ? response.data.donationHistory : [],
      };
    } catch (error) {
      return { enrollment: null, donationHistory: [] };
    }
  }

  async saveNityaSevaEnrollment(
    payload: NityaSevaEnrollmentPayload,
    signal?: AbortSignal,
  ): Promise<NityaSevaEnrollment> {
    const response = await this.client.post<{ enrollment: NityaSevaEnrollment }>(
      '/user/nityaseva/enrollment',
      payload,
      { signal },
    );
    return response.data.enrollment;
  }

  async pauseNityaSevaEnrollment(signal?: AbortSignal): Promise<NityaSevaEnrollment> {
    const response = await this.client.post<{ enrollment: NityaSevaEnrollment }>(
      '/user/nityaseva/enrollment/pause',
      {},
      { signal },
    );
    return response.data.enrollment;
  }

  async createNityaSevaDuePaymentOrder(
    payload: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<RazorpayOrder> {
    const response = await this.client.post<RazorpayOrder>(
      '/devotee/donation',
      payload,
      { signal },
    );
    return response.data;
  }

  async verifyNityaSevaDuePayment(
    payload: PaymentVerificationPayload,
    signal?: AbortSignal,
  ): Promise<PaymentVerificationResponse> {
    const response = await this.client.post<PaymentVerificationResponse>(
      '/public/payment/razorpay/verify',
      payload,
      { signal },
    );
    return response.data;
  }
}

export const subscriptionsService = new SubscriptionsService(ApiClient.getInstance(API_BASE_URL));
